import {
  useAuth,
  getUserAttributes,
  getUserDisplayName,
} from '@choco-admin/auth';
import { getApolloClient } from '@choco-admin/graphql-client';
import {
  Navbar,
  NavbarContent,
  NavbarItem,
  Button,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@heroui/react';
import React, { useState, useEffect } from 'react';
import { FiMenu, FiLogOut } from 'react-icons/fi';

import { PuppyModeToggle } from '../../components/PuppyModeToggle';
import { ThemeToggle } from '../../components/ThemeToggle';
import { useTheme } from '../../contexts/ThemeContext';

import { SupportCodeButton } from './SupportCodeButton';

interface HeaderProps {
  onToggleSidebar: () => void;
  onToggleMobileMenu: () => void;
  sidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  onToggleMobileMenu,
  sidebarCollapsed,
}) => {
  const { signOut, user } = useAuth();
  const { theme } = useTheme();
  const [userDisplayName, setUserDisplayName] = useState('Admin User');
  const [userInitials, setUserInitials] = useState('AU');

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const attributes = await getUserAttributes();
        const displayName = getUserDisplayName(attributes);
        setUserDisplayName(displayName);

        // Generate initials from display name
        const names = displayName.split(' ');
        const initials =
          names.length >= 2
            ? `${names[0][0]}${names[names.length - 1][0]}`
            : `${names[0][0]}${names[0][1] || ''}`;
        setUserInitials(initials.toUpperCase());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading user info:', error);
      }
    };

    if (user) {
      loadUserInfo();
    }
  }, [user]);

  const handleMenuClick = () => {
    // On mobile, toggle mobile menu; on desktop, toggle sidebar
    if (window.innerWidth <= 1024) {
      onToggleMobileMenu();
    } else {
      onToggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Clear Apollo cache so no stale data leaks to a subsequent login
      const client = getApolloClient();
      if (client) {
        await client.clearStore();
      }
      // User will be redirected to login page by the AuthGuard
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Logout error:', error);
    }
  };

  return (
    <Navbar
      maxWidth="full"
      className="border-b border-default-200 bg-background shadow-sm backdrop-blur-md h-16"
      id="header"
      style={{
        background:
          theme === 'dark'
            ? 'linear-gradient(135deg, rgba(24,0,63,0.95) 0%, rgba(26,13,120,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <NavbarContent justify="start">
        <NavbarItem className="lg:hidden">
          <Button
            isIconOnly
            variant="light"
            onPress={handleMenuClick}
            aria-label="Toggle menu"
            className="hover:bg-default-100 transition-colors border border-transparent hover:border-default-200 text-default-700 hover:text-foreground"
            size="lg"
          >
            <FiMenu size={22} className="text-default-700" />
          </Button>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent justify="end" className="gap-4">
        <NavbarItem>
          <SupportCodeButton />
        </NavbarItem>
        <NavbarItem>
          <PuppyModeToggle size="md" />
        </NavbarItem>
        <NavbarItem>
          <ThemeToggle />
        </NavbarItem>

        {/* User Info Section */}
        <NavbarItem className="hidden lg:flex">
          <div className="flex items-center mr-3">
            <span className="text-sm font-bold text-foreground">
              {userDisplayName}
            </span>
          </div>
        </NavbarItem>

        <NavbarItem>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Avatar
                size="md"
                name={userInitials}
                className="bg-gradient-to-br from-primary-500 to-primary-600 text-white cursor-pointer hover:shadow-lg transition-all border-2 border-content1 shadow-md"
              />
            </DropdownTrigger>
            <DropdownMenu
              aria-label="User menu actions"
              className="w-64"
              itemClasses={{
                base: 'gap-4 p-3',
              }}
            >
              <DropdownItem
                key="profile-info"
                isReadOnly
                className="h-16 opacity-100"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-semibold text-foreground">
                      {userDisplayName}
                    </p>
                  </div>
                </div>
              </DropdownItem>
              <DropdownItem
                key="logout"
                color="danger"
                startContent={<FiLogOut size={18} />}
                onPress={handleLogout}
                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <span className="font-medium">Sign Out</span>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarItem>
      </NavbarContent>
    </Navbar>
  );
};

export default Header;
